export function FullPageLoader({ title = "Loading" }) {
  return (
    <div className="flex min-h-[70vh] w-full items-center justify-center">
      <div className="relative flex flex-col items-center gap-5 text-center">
        {/* Ambient coral glow */}
        <div className="pointer-events-none absolute -inset-32 animate-pulse rounded-full bg-[#2563EB]/[0.04]" />

        {/* Animated Liquid C icon */}
        <div className="relative z-10">
          <svg width="88" height="88" viewBox="0 0 100 100" fill="none">
            <defs>
              <filter id="gSplash" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              <linearGradient id="fluidSplash" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2563EB"/>
                <stop offset="100%" stopColor="#60A5FA"/>
              </linearGradient>
            </defs>
            {/* Outer ring — pulsing */}
            <circle cx="50" cy="50" r="42" stroke="#2563EB" strokeWidth="0.5" fill="none" className="loader-ring" />
            {/* Rotating ticks */}
            <g className="loader-spin" opacity="0.12">
              <line x1="50" y1="4" x2="50" y2="10" stroke="#2563EB" strokeWidth="1" strokeLinecap="round"/>
              <line x1="50" y1="90" x2="50" y2="96" stroke="#2563EB" strokeWidth="1" strokeLinecap="round"/>
              <line x1="4" y1="50" x2="10" y2="50" stroke="#2563EB" strokeWidth="1" strokeLinecap="round"/>
              <line x1="90" y1="50" x2="96" y2="50" stroke="#2563EB" strokeWidth="1" strokeLinecap="round"/>
            </g>
            {/* Morphing blob */}
            <path className="loader-morph" d="M44,14 C22,6 6,26 6,50 C6,74 24,92 46,86 C56,83 60,76 58,66 C55,52 48,50 50,50 C52,50 56,48 58,36 C60,26 56,18 44,14 Z" fill="url(#fluidSplash)" opacity="0.15" />
            {/* C letterform */}
            <path d="M58,28 C38,18 18,34 18,50 C18,66 38,82 58,72" stroke="url(#fluidSplash)" strokeWidth="5" strokeLinecap="round"/>
            {/* Floating particles */}
            <g className="loader-float-1"><circle cx="72" cy="24" r="3" fill="#2563EB" filter="url(#gSplash)" opacity="0.8"/></g>
            <g className="loader-float-2"><circle cx="78" cy="70" r="2.5" fill="#60A5FA" filter="url(#gSplash)" opacity="0.5"/></g>
            <g className="loader-float-3"><circle cx="12" cy="40" r="2" fill="#2563EB" filter="url(#gSplash)" opacity="0.4"/></g>
            {/* Core pulse */}
            <circle cx="50" cy="50" r="5.5" fill="#2563EB" filter="url(#gSplash)" className="loader-core"/>
            <circle cx="50" cy="50" r="2.2" fill="hsl(var(--background))"/>
          </svg>
        </div>

        {/* Loading text */}
        <p
          className="relative z-10 loader-text-fade"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "10px",
            letterSpacing: "4px",
            textTransform: "uppercase",
            color: "hsl(var(--muted-foreground))",
          }}
        >
          {title}
        </p>

        {/* Loading bar */}
        <div className="relative z-10 h-[2px] w-[120px] overflow-hidden rounded-full" style={{ background: "hsl(var(--border))" }}>
          <div className="loader-bar h-full w-[40%] rounded-full bg-[#2563EB]" />
        </div>
      </div>

      <style>{`
        .loader-spin { animation: loaderSpin 30s linear infinite; transform-origin: 50px 50px; }
        .loader-ring { animation: loaderRing 4s ease-in-out infinite; }
        .loader-morph { animation: loaderMorph 8s ease-in-out infinite; }
        .loader-core { animation: loaderCorePulse 3s ease-in-out infinite; }
        .loader-float-1 { animation: loaderFloat 5s ease-in-out infinite; }
        .loader-float-2 { animation: loaderFloat 5s ease-in-out infinite; animation-delay: 1.2s; }
        .loader-float-3 { animation: loaderFloat 5s ease-in-out infinite; animation-delay: 2.8s; }
        .loader-text-fade { animation: loaderFadeInOut 2s ease-in-out infinite; }
        .loader-bar { animation: loaderSlide 1.5s ease-in-out infinite; }
        @keyframes loaderSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes loaderRing { 0%, 100% { opacity: 0.08; } 50% { opacity: 0.14; } }
        @keyframes loaderMorph {
          0%,100%{d:path("M44,14 C22,6 6,26 6,50 C6,74 24,92 46,86 C56,83 60,76 58,66 C55,52 48,50 50,50 C52,50 56,48 58,36 C60,26 56,18 44,14 Z")}
          25%{d:path("M46,12 C20,4 4,28 4,50 C4,72 22,94 48,88 C58,85 62,76 60,64 C57,50 50,48 50,50 C50,52 54,50 60,38 C64,26 58,16 46,12 Z")}
          50%{d:path("M42,16 C24,8 8,24 8,50 C8,76 26,90 44,84 C54,81 58,74 56,68 C53,54 46,52 50,50 C54,48 58,46 56,34 C54,24 48,18 42,16 Z")}
          75%{d:path("M48,13 C22,3 5,27 5,50 C5,73 23,93 47,87 C57,84 61,75 59,65 C56,51 49,49 50,50 C51,51 55,49 59,37 C62,25 57,17 48,13 Z")}
        }
        @keyframes loaderCorePulse { 0%, 100% { opacity: 0.85; } 50% { opacity: 1; } }
        @keyframes loaderFloat {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.6; }
          33% { transform: translateY(-8px) translateX(3px); opacity: 1; }
          66% { transform: translateY(-3px) translateX(-4px); opacity: 0.4; }
        }
        @keyframes loaderFadeInOut { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes loaderSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
      `}</style>
    </div>
  );
}
